import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '../api/endpoints'
import { useI18n } from '../i18n/I18nProvider'
import { Busy, ErrorBanner, Field, Notice, TextArea } from '../ui/controls'
import { Band } from '../ui/Band'
import type { MessageKey } from '../i18n/messages'
import type { MatchReport, ReportItem } from '../api/types'

/**
 * L2 — what twenty dollars, or ten, actually buys.
 *
 * Every row reads **我的要求，对方的情况**（Nai, 2026-09-20）: the reader's own condition on the
 * left, the other person's situation on the right, and a band for the two together. Not a score:
 * 决议 V1.2 §3.3 keeps exact numbers out of the paid report as well.
 *
 * Two things are absent rather than hidden — the other person's legal name and their contact
 * details are not fields on the API's record at all (决议 §2.1: 任何付费都买不到). The page says
 * so out loud, because "complete report" would otherwise be read as a promise it does not make.
 */
export function MatchReportPage() {
  const { t } = useI18n()
  const { matchId = '' } = useParams()
  const [params] = useSearchParams()

  const [report, setReport] = useState<MatchReport | null>(null)
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const reInvite = params.get('reinvite') === '1'

  useEffect(() => {
    api.matching.report(matchId).then(setReport).catch(setError)
  }, [matchId])

  if (!report) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  const canInvite = report.state === 'Unlocked' || (reInvite && report.state === 'CancelledByMe')

  const invite = (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setBusy(true)

    const note = message.trim() === '' ? null : message.trim()

    const request = reInvite
      ? api.invitations.reInvite(matchId, note)
      : api.invitations.send(matchId, note)

    request
      .then(() => {
        setSent(true)
        return api.matching.report(matchId).then(setReport)
      })
      .catch(setError)
      .finally(() => setBusy(false))
  }

  return (
    <div className="page stack">
      <h1>{t('report.title')}</h1>

      <ErrorBanner error={error} />
      {sent ? <Notice>{t('report.inviteSent')}</Notice> : null}

      <section className="card stack">
        <div className="photo-row">
          {report.profile.primaryPhotoId ? (
            <img
              className="photo-preview"
              src={api.photo.url(report.profile.primaryPhotoId)}
              alt=""
            />
          ) : null}

          <div className="stack">
            <strong className="big-name">{report.profile.displayName}</strong>
            <span className={`state state-${report.state.toLowerCase()}`}>
              {t(`state.${report.state}` as MessageKey)}
            </span>
          </div>
        </div>

        <div className="kv">
          <span>{t('report.overall')}</span>
          <Band value={report.overallBand} />
        </div>

        <div className="kv">
          <span>{t('report.theyFitMe')}</span>
          <Band value={report.theyFitMe} />
        </div>

        {/*
          Both directions are shown here, unlike L1.5 where only one is — by then the reader has
          paid and is deciding about a person, not being handed a verdict on themselves.
        */}
        <div className="kv">
          <span>{t('report.iFitThem')}</span>
          <Band value={report.iFitThem} />
        </div>
      </section>

      {report.profile.selfIntroduction || report.profile.hobbiesText ? (
        <section className="card stack">
          {report.profile.selfIntroduction ? <p>{report.profile.selfIntroduction}</p> : null}
          {report.profile.hobbiesText ? (
            <p className="field-hint">{report.profile.hobbiesText}</p>
          ) : null}
        </section>
      ) : null}

      <section className="card">
        <table className="report-table">
          <thead>
            <tr>
              <th>{t('report.myRequirement')}</th>
              <th>{t('report.theirSituation')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {report.items.map((item) => (
              <ReportRow key={item.field} item={item} />
            ))}
          </tbody>
        </table>
      </section>

      <Notice>{t('report.noName')}</Notice>

      {canInvite ? (
        <form className="card stack" onSubmit={invite}>
          <Field label={t('report.note')} hint={t('report.notePlaceholder')}>
            <TextArea value={message} onChange={setMessage} rows={4} maxLength={200} />
          </Field>

          <button type="submit" className="primary" disabled={busy}>
            {busy ? t('common.saving') : t('report.invite')}
          </button>

          <p className="field-hint">{t('invitations.sevenDays')}</p>
        </form>
      ) : null}
    </div>
  )
}

function ReportRow({ item }: { item: ReportItem }) {
  const { t } = useI18n()

  return (
    <tr>
      <td>
        <strong>{item.field}</strong>
        <div className="field-hint">
          {t(`importance.${item.myCondition.importance}` as MessageKey)}
        </div>
      </td>

      <td>
        {/*
          Hidden means they set the field to Private: the value is withheld and the fit is not.
          Withholding the band too would punish the reader for someone else's privacy choice,
          which is not what 决议 §5.2 asks for.
        */}
        {item.theirValue.hidden ? (
          <em className="field-hint">{t('report.hidden')}</em>
        ) : (
          <>
            {item.theirValue.values.map((value) => (
              <span className="chip" key={value.id}>
                {value.code ?? value.id}
              </span>
            ))}
            {item.theirValue.numbers.map((number) => (
              <span className="chip" key={number.kind}>
                {number.kind}: {number.value}
              </span>
            ))}
          </>
        )}
      </td>

      <td>
        <Band value={item.band} />
      </td>
    </tr>
  )
}
